import { isValid, parseDate, toIsoString } from "@awe-web/shared/lib";
import { loadStations } from "@awe-web/shared/lib";
import { newTranslate } from "@awe-web/shared/lib";
import {
  getContractTypeForB2bLTR,
  getContractTypeForB2bSTR,
  getContractTypeForB2cLTR,
  getContractTypeForB2cSTR,
  getMinDaysForB2bLtr,
  getMinDaysForB2cLtr
} from "../config/store.js";
import {
  B2B_LTR_SelectRentObject,
  B2B_STR_SelectRentObject,
  B2C_STR_SelectRentObject,
  B2C_LTR_SelectRentObject
} from "../routing/routes.js";
import { getCoDateTime, getCiDateTime, getRawCiStation, getRawCoStation, getSelectStationAndTimeFormValidate, BMW_SELECTED_STATION_AND_TIME } from "./store.js";
import { isLtr } from "../rent_contracts/util.js";

const SELECT_STATION_AND_TIME_FORM_CO_STATION_ID_LABEL = "FSD0000081";
const SELECT_STATION_AND_TIME_FORM_CO_STATION_ID_PLACEHOLDER = "FSD0000153";
const SELECT_STATION_AND_TIME_FORM_CI_STATION_ID_LABEL = "FSD0000082";
const SELECT_STATION_AND_TIME_FORM_CI_STATION_ID_PLACEHOLDER = "FSD0000086";
const SELECT_STATION_AND_TIME_FORM_CO_DATE_TIME_LABEL = "FSD0000083";
const SELECT_STATION_AND_TIME_FORM_CO_DATE_TIME_PLACEHOLDER = "FSD0000154";
const SELECT_STATION_AND_TIME_FORM_CI_DATE_TIME_LABEL = "FSD0000084";
const SELECT_STATION_AND_TIME_FORM_CI_DATE_TIME_PLACEHOLDER = "FSD0000155";

const validateStationId = async (context, stationId) => {
  if (!stationId) {
    return undefined;
  }

  try {
    await context.api.stations.getStation({ stationId });
    return stationId;
  }
  catch (error) {
    return undefined;
  }
};

export const initializeSelectStationAndTimeForm = ({ reservationPath = "B2C" } = {}) => async (dispatch, getState, context) => {
  const translate = newTranslate(getState());
  const stateCOStationId = getRawCoStation(getState()).get('id');
  const stateCIStationId = getRawCiStation(getState()).get('id');
  const stateCODateTime = getCoDateTime(getState());
  const stateCIDateTime = getCiDateTime(getState());

  const validate = getSelectStationAndTimeFormValidate(getState());
  const contractTypes = reservationPath === "B2C"
    ? [getContractTypeForB2cSTR(getState()), getContractTypeForB2cLTR(getState())]
    : [getContractTypeForB2bSTR(getState()), getContractTypeForB2bLTR(getState())];

  const [coStationId, ciStationIdCandidate] = await Promise.all([
    validateStationId(context, stateCOStationId),
    validateStationId(context, stateCIStationId)
  ]);
  const ciStationId = ciStationIdCandidate || coStationId;

  const coDateTime = parseDate(stateCODateTime);
  const ciDateTime = parseDate(stateCIDateTime);

  const errors = validate({ coStationId, ciStationId, coDateTime, ciDateTime });

  await dispatch(loadStations(contractTypes));

  dispatch({
    type: "USER/FORM_CONFIGURATION_LOADED",
    payload: {
      groupName: "selectStationAndTime",
      formConfiguration: [
        {
          items: [
            {
              name: "coStationId",
              value: coStationId,
              fieldLabel: translate(SELECT_STATION_AND_TIME_FORM_CO_STATION_ID_LABEL),
              emptyText: translate(SELECT_STATION_AND_TIME_FORM_CO_STATION_ID_PLACEHOLDER)
            },
            {
              name: "ciStationId",
              value: ciStationId,
              fieldLabel: translate(SELECT_STATION_AND_TIME_FORM_CI_STATION_ID_LABEL),
              emptyText: translate(SELECT_STATION_AND_TIME_FORM_CI_STATION_ID_PLACEHOLDER)
            },
            {
              name: "coDateTime",
              value: errors.coDateTime ? undefined : toIsoString(coDateTime),
              fieldLabel: translate(SELECT_STATION_AND_TIME_FORM_CO_DATE_TIME_LABEL),
              emptyText: translate(SELECT_STATION_AND_TIME_FORM_CO_DATE_TIME_PLACEHOLDER)
            },
            {
              name: "ciDateTime",
              value: errors.ciDateTime ? undefined : toIsoString(ciDateTime),
              fieldLabel: translate(SELECT_STATION_AND_TIME_FORM_CI_DATE_TIME_LABEL),
              emptyText: translate(SELECT_STATION_AND_TIME_FORM_CI_DATE_TIME_PLACEHOLDER)
            }
          ]
        }
      ]
    }
  });
};

export const submitSelectStationAndTimeForm = (formValues, { reservationPath = "B2C" }) => async (dispatch, getState) => {
  const { coStationId, ciStationId, coDateTime, ciDateTime } = formValues;

  // store the selected values
  await dispatch(setCoStation(coStationId));
  await dispatch(setCiStation(ciStationId));
  const coDateTimeStr = dispatch(setCoDateTime(toIsoString(coDateTime)));
  const ciDateTimeStr = dispatch(setCiDateTime(toIsoString(ciDateTime)));

  sessionStorage.setItem(BMW_SELECTED_STATION_AND_TIME, JSON.stringify({
    coStation: { id: coStationId },
    ciStation: { id: ciStationId },
    coDateTime: coDateTimeStr,
    ciDateTime: ciDateTimeStr
  }));


  let nextLocation;

  if (reservationPath === "B2C") {
    nextLocation = isLtr(coDateTime, ciDateTime, getMinDaysForB2cLtr(getState()))
      ? B2C_LTR_SelectRentObject
      : B2C_STR_SelectRentObject;
  }
  else {
    nextLocation = isLtr(coDateTime, ciDateTime, getMinDaysForB2bLtr(getState()))
      ? B2B_LTR_SelectRentObject
      : B2B_STR_SelectRentObject;
  }

  return nextLocation.path();
};

export const setCoStation = (stationId) => async (dispatch, getState, context) => {
  const station = await context.api.stations.getStation({ stationId });

  dispatch({
    type: "STATIONS/CO_STATION_SET",
    payload: { station }
  });
};

export const setCiStation = (stationId) => async (dispatch, getState, context) => {
  const station = await context.api.stations.getStation({ stationId });

  dispatch({
    type: "STATIONS/CI_STATION_SET",
    payload: { station }
  });
};

export const setCoDateTime = (dateTimeString) => (dispatch) => {
  if (isValid(dateTimeString)) {
    dispatch({
      type: "STATIONS/CO_DATE_TIME_SET",
      payload: {
        dateTime: dateTimeString
      }
    });
    return dateTimeString;
  }
};

export const setCiDateTime = (dateTimeString) => (dispatch) => {
  if (isValid(dateTimeString)) {
    dispatch({
      type: "STATIONS/CI_DATE_TIME_SET",
      payload: {
        dateTime: dateTimeString
      }
    });
    return dateTimeString;
  }
};
